import classNames from 'classnames';
import { Table } from 'components/atoms/Table';
import {
  Sort,
  type onSort,
  type sorter,
} from 'components/atoms/Table/table.types';
import { TableAnimatableRow } from 'components/atoms/Table/TableAnimatableRow';
import {
  getRowAnimationProps,
  getRowDefaultProps,
  getSortProps,
} from 'components/atoms/Table/utils';
import UseEffect from 'components/atoms/UseEffect';
import { useMemo, type FC, type ReactNode } from 'react';
import type { color } from 'types/shared/worlds';

const theme = {
  taxes: {
    className: 'bg-white hover:!bg-taxes-100',
    highlightClassName: '!bg-taxes-100',
  },
  bank: {
    className: 'bg-white hover:!bg-bank-100',
    highlightClassName: '!bg-bank-100',
  },
  expenses: {
    className: 'bg-white hover:!bg-expenses-100',
    highlightClassName: '!bg-expenses-100',
  },
  revenues: {
    className: 'bg-white hover:!bg-revenues-100',
    highlightClassName: '!bg-revenues-100',
  },
  primary: {
    className: 'bg-white hover:!bg-primary-100',
    highlightClassName: '!bg-primary-100',
  },
};

export type TableCellRendererProps = {
  list: any[];
  item: any;
  index: number;
  selected?: string[];
  setSelected?: (ids: string[]) => void;
  lastToggledIndex?: number;
  setLastToggledIndex: (index: number) => void;
  selectableRowsIds: string[];
};

export type TableCellLabelProps = Omit<
  TableCellRendererProps,
  'item' | 'index'
>;

type TableColumnDefinition = {
  key: string;
  sortableClassName?: string;
  isSortable?: boolean;
  contentClassName?: string;
  thClassName?: string;
  Label: string | FC<TableCellLabelProps>;
  Renderer: FC<TableCellRendererProps>;
  hidden?: boolean;
  onClick?: (e: any) => any;
};

const TableBuilder: FC<{
  hideTableHead?: boolean;
  columns: TableColumnDefinition[];
  data: any[];
  sorter?: sorter;
  onSort?: onSort;
  onRowClick?: (_id: string, item?: any) => void;
  footer?: ReactNode;
  highlight?: string[];
  disableRowAnimation?: boolean;
  selected?: string[];
  setSelected?: (ids: string[]) => void;
  lastToggledIndex?: number;
  setLastToggledIndex?: (index: number) => void;
  selectableRowsIds?: string[];
  afterFlash?: (id: string) => void;
  flash?: string[];
  color?: color;
  tableHeadClassName?: string;
}> = ({
  hideTableHead,
  columns,
  data,
  sorter = { cellId: '', direction: Sort.asc },
  onSort = () => null,
  onRowClick = () => null,
  footer,
  highlight,
  disableRowAnimation,
  selected,
  setSelected,
  lastToggledIndex,
  setLastToggledIndex = () => null,
  selectableRowsIds = [],
  afterFlash,
  flash,
  color = 'bank',
  tableHeadClassName,
}) => {
  const fixedAnimationProps = getRowDefaultProps();

  const _theme = theme[color];

  const _columns = useMemo(
    () => columns.filter((col) => !col.hidden),
    [columns],
  );

  return (
    <Table
      className={classNames({
        '!border-none !bg-transparent !border-transparent':
          hideTableHead && !data.length,
      })}
      innerWrapperClassName={classNames({
        '!border-none !bg-transparent !border-transparent':
          hideTableHead && !data.length,
      })}
    >
      <Table.Head
        hidden={hideTableHead}
        className={classNames(
          '!bg-primary-50 rounded-full',
          tableHeadClassName,
        )}
      >
        {_columns.map(
          (
            {
              isSortable,
              key,
              Label,
              sortableClassName,
              contentClassName,
              thClassName,
              onClick,
            },
            colIndex,
          ) => {
            const sortProps = isSortable
              ? { ...getSortProps(key, sorter, onSort) }
              : {};

            const LabelComponent =
              typeof Label === 'string' ? (
                Label
              ) : (
                <Label
                  selected={selected}
                  setSelected={setSelected}
                  list={data}
                  lastToggledIndex={lastToggledIndex}
                  setLastToggledIndex={setLastToggledIndex}
                  selectableRowsIds={selectableRowsIds}
                />
              );

            return (
              <Table.HeadCell
                key={key}
                {...sortProps}
                sortableClassName={sortableClassName}
                contentClassName={contentClassName}
                onClick={onClick}
                className={classNames(
                  { 'rounded-tl-lg': colIndex === 0 },
                  { 'rounded-tr-lg': colIndex === _columns.length - 1 },
                  thClassName,
                )}
              >
                {LabelComponent}
              </Table.HeadCell>
            );
          },
        )}
      </Table.Head>

      <Table.Body className="bg-white">
        {data.map((item, index) => {
          const { _id } = item;

          const shouldHighlight = highlight?.includes(_id);

          const shouldFlash = flash?.includes(_id);

          const activeAnimationProps = getRowAnimationProps(index);

          return (
            <UseEffect
              key={_id}
              effect={() => {
                const id = setTimeout(() => afterFlash?.(item._id), 4000);

                return () => clearTimeout(id);
              }}
              deps={[shouldHighlight, shouldFlash]}
              render={() => (
                <TableAnimatableRow
                  {...(disableRowAnimation
                    ? fixedAnimationProps
                    : activeAnimationProps)}
                  key={_id}
                  className={classNames(
                    'group transition-colors duration-200',
                    _theme.className,
                    {
                      [_theme.highlightClassName]:
                        shouldHighlight || shouldFlash,
                    },
                  )}
                  onClick={() => onRowClick?.(_id, item)}
                >
                  {_columns.map((col, colIndex) => {
                    const isLastRow = index === data.length - 1;

                    const isLastRowFirstCell = isLastRow && colIndex === 0;

                    const isLastRowLastCell =
                      isLastRow && colIndex === _columns.length - 1;

                    return (
                      <Table.Cell
                        key={col.key}
                        className={classNames({
                          '!rounded-bl-xl': isLastRowFirstCell,
                          '!rounded-br-xl': isLastRowLastCell,
                        })}
                      >
                        <col.Renderer
                          item={item}
                          index={index}
                          selected={selected}
                          setSelected={setSelected}
                          list={data}
                          lastToggledIndex={lastToggledIndex}
                          setLastToggledIndex={setLastToggledIndex}
                          selectableRowsIds={selectableRowsIds}
                        />
                      </Table.Cell>
                    );
                  })}
                </TableAnimatableRow>
              )}
            />
          );
        })}
      </Table.Body>

      {footer && <Table.Footer>{footer}</Table.Footer>}
    </Table>
  );
};

export default TableBuilder;
